import axios from 'axios'
import { prependQuery, appendToFormData } from '../helpers/utility'
import { apiKey } from '../config/apiKey'
import { getAuthToken } from '../store/configStore'

//
// ─── SETTINGS ───────────────────────────────────────────────────────────────────
//

let baseApiUrl = ''

if (process.env.NODE_ENV === 'production') {
  // main server with domain:
  baseApiUrl = 'https://api.mabna.school'
} else {
  baseApiUrl = 'https://api.mabna.school' // 'https://mabna.school' // dev server:
}

// general config
const generalConfig = {
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded',
    'x-api-key': apiKey,
  },
  // always return the response even with errors
  validateStatus: status => true,
}

// authorized header config
export const postConfig = () => {
  return {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: 'Token ' + getAuthToken(),
      'x-api-key': apiKey,
      Accept: '*/*',
    },
    // always return the response even with errors
    validateStatus: status => true,
  }
}

//
// ─── API CLASS ──────────────────────────────────────────────────────────────────
//

export { baseApiUrl }

export default class Api {
  //
  // ─── COMMON END POINTS ──────────────────────────────────────────────────────────
  //

  static genericImageUpload(body) {
    return axios.post(`${baseApiUrl}/common/image/add/`, body, postConfig())
  }

  static getService(serviceCode) {
    return axios.get(`${baseApiUrl}/orders/service/code/${serviceCode}/`, postConfig())
  }

  //
  // ─── AUTHENTICATION  ACTIONS ────────────────────────────────────────────────────
  //

  static login(username, password) {
    let formData = new FormData()
    formData.append('username', username)
    formData.append('password', password)

    return axios.post(`${baseApiUrl}/accounts/login/`, formData, generalConfig)
  }

  static loginAs(username) {
    let formData = new FormData()
    formData.append('username', username)
    return axios.post(`${baseApiUrl}/accounts/login-as/`, formData, postConfig())
  }

  // needs to connect to the server API is not ready yet
  // the sole purpose of timeout here is to compensate for aSync-ness of
  // removing user data from local-storage 🙇
  static logout() {
    return new Promise(resolve => {
      setTimeout(() => resolve({}), 200)
    })
  }

  static signup(formData, userType) {
    // manager is logged in when creating a new user.
    const config = getAuthToken() ? postConfig() : generalConfig

    switch (userType) {
      case 'STUDENT':
        return axios.post(`${baseApiUrl}/profiles/students/signup/`, formData, config)

      case 'TEACHER':
        return axios.post(`${baseApiUrl}/profiles/teachers/signup/`, formData, config)

      case 'SCHOOL':
        return axios.post(`${baseApiUrl}/profiles/schools/signup/`, formData, config)

      case 'SUPERVISOR':
        return axios.post(`${baseApiUrl}/profiles/supervisors/signup/`, formData, config)

      case 'APP':
        return axios.post(`${baseApiUrl}/apps/application/`, formData, config)

      default:
        return Promise.reject({ _error: 'Check userType!' })
    }
  }

  static resetPass(formData) {
    return axios.post(`${baseApiUrl}/auth/password/reset/`, formData, generalConfig)
  }

  static sendConfirmationEmail() {
    return axios.post(
      `${baseApiUrl}/auth/email/request-verification/`,
      null,
      postConfig()
    )
  }

  static sendConfirmationSms() {
    return axios.post(
      `${baseApiUrl}/auth/mobile/request-verification/`,
      null,
      postConfig()
    )
  }

  static verifyEmail(formData, auth) {
    return axios.post(`${baseApiUrl}/auth/email/verify/`, formData, postConfig(auth))
  }

  static verifyPhone(formData) {
    //  is the code send to user's mobile phone
    return axios.post(`${baseApiUrl}/auth/mobile/verify/`, formData, postConfig())
  }

  static resetPassBySms(formData) {
    return axios.post(`${baseApiUrl}/auth/password/sms/reset/`, formData, generalConfig)
  }

  static confirmResetPass(formData) {
    return axios.post(
      `${baseApiUrl}/auth/password/reset/confirm/`,
      formData,
      generalConfig
    )
  }

  static confirmResetPassBySms(formData) {
    // username, new_password, code that from actual SMS
    return axios.post(
      `${baseApiUrl}/auth/password/sms/reset/confirm/`,
      formData,
      generalConfig
    )
  }

  //
  // ─── PROFILE MANAGEMENT BY USER ACTIONS ──────────────────────────
  //

  static fetchUserProfile(userId, userType) {
    switch (userType) {
      case 'STUDENT':
        return axios.get(`${baseApiUrl}/profiles/students/${userId}/`, postConfig())

      case 'TEACHER':
        return axios.get(`${baseApiUrl}/profiles/teachers/${userId}/`, postConfig())

      case 'SCHOOL':
        return axios.get(`${baseApiUrl}/profiles/schools/${userId}/`, postConfig())

      case 'SUPERVISOR':
        return axios.get(`${baseApiUrl}/profiles/supervisors/${userId}/`, postConfig())

      case 'APPLICATION':
        return axios.get(`${baseApiUrl}/apps/application/${userId}/`, postConfig())

      default:
        Promise.reject({
          error: `fetchUserProfile: api.js: end point is not defined for ${userType}`,
        })
    }
  }

  static changeEmail(formData) {
    return axios.post(`${baseApiUrl}/accounts/change-email/`, formData, postConfig())
  }

  static changePassword(formData) {
    return axios.post(`${baseApiUrl}/accounts/change-password/`, formData, postConfig())
  }

  static updateProfile(formData, userId, userType) {
    switch (userType) {
      case 'STUDENT':
        return axios.patch(
          `${baseApiUrl}/profiles/students/update/${userId}/`,
          formData,
          postConfig()
        )

      case 'SUPERVISOR':
        return axios.patch(
          `${baseApiUrl}/profiles/supervisors/update/${userId}/`,
          formData,
          postConfig()
        )

      case 'TEACHER':
        return axios.patch(
          `${baseApiUrl}/profiles/teachers/update/${userId}/`,
          formData,
          postConfig()
        )

      case 'SCHOOL':
        return axios.patch(
          `${baseApiUrl}/profiles/schools/update/${userId}/`,
          formData,
          postConfig()
        )

      case 'APPLICATION':
        return axios.patch(
          `${baseApiUrl}/apps/application/${userId}/`,
          formData,
          postConfig()
        )

      default:
        console.log('not able to find userType')
    }
  }

  static fetchUserProfileDocumentsGroup() {
    return axios.get(
      `${baseApiUrl}/profiles/documents/group/`,
      postConfig(),
    )
  }

  static uploadDocumentToProfile(formData) {
    return axios.post(
      `${baseApiUrl}/profiles/documents/document/`,
      formData,
      postConfig()
    )
  }

  static updateDocumentOnProfile(documentId, formData) {
    return axios.post(
      `${baseApiUrl}/profiles/documents/document/${documentId}/update/`,
      formData,
      postConfig()
    )
  }

  static removeDocumentFromProfile(documentId) {
    return axios.delete(
      `${baseApiUrl}/profiles/documents/document/${documentId}/update/`,
      postConfig()
    )
  }


  //
  // ─── GENERAL FETCH ACTIONS ──────────────────────────────────────────────────────
  //

  static fetchGrades() {
    return axios.get(
      `${baseApiUrl}/educations/grades/`,
      getAuthToken() ? postConfig() : generalConfig
    )
  }

  static fetchLevels() {
    return axios.get(
      `${baseApiUrl}/educations/levels/`,
      getAuthToken() ? postConfig() : generalConfig
    )
  }

  static fetchAcademicYears() {
    return axios.get(`${baseApiUrl}/educations/academic-years/`, postConfig())
  }

  static fetchCities(stateId) {
    return axios.get(`${baseApiUrl}/common/cities/?state=${stateId}`, generalConfig)
  }

  static fetchDistricts(stateId) {
    return axios.get(`${baseApiUrl}/common/districts/`, {
      ...generalConfig,
      params: {
        state: stateId,
      },
    })
  }

  // "geographical" states. not to be confused with app state
  static fetchStates() {
    return axios.get(`${baseApiUrl}/common/states/`, generalConfig)
  }

  static addSchool(formData) {
    return axios.post(`${baseApiUrl}/profiles/schools/add/`, formData, postConfig())
  }

  static fetchSchools(formData) {
    return axios.post(`${baseApiUrl}/profiles/schools/search/`, formData, generalConfig)
  }

  // there is a unique code called "SENAD" which identifies a school.
  static fetchSchoolInfoByCode(schoolCode) {
    if (!schoolCode) return Promise.reject('schoolCode is a falsy value')

    return axios.get(
      `${baseApiUrl}/profiles/schools/code/${schoolCode.trim()}/`,
      generalConfig
    )
  }

  static getStudentsList(queryString) {
    return axios.get(
      `${baseApiUrl}/profiles/students/${prependQuery(queryString)}`,
      postConfig()
    )
  }

  static getSchoolsList(queryString) {
    return axios.get(
      `${baseApiUrl}/profiles/schools/${prependQuery(queryString)}`,
      postConfig()
    )
  }

  static getTeachersList(queryString) {
    return axios.get(
      `${baseApiUrl}/profiles/teachers/${prependQuery(queryString)}`,
      postConfig()
    )
  }

  static getSupervisorsList(queryString) {
    return axios.get(
      `${baseApiUrl}/profiles/supervisors/${prependQuery(queryString)}`,
      postConfig()
    )
  }

  static getAppsList(queryString) {
    return axios.get(
      `${baseApiUrl}/apps/application/list/${prependQuery(queryString)}`,
      postConfig()
    )
  }

  static getCourseTopics() {
    return axios.get(`${baseApiUrl}/educations/course-topics/`, postConfig())
  }

  static getFeaturedCourses() {
    return axios.get(`${baseApiUrl}/courses/course/featured/`, postConfig())
  }

  static getFeaturedEvents() {
    return axios.get(`${baseApiUrl}/events/event/featured/`, postConfig())
  }

  static getFeaturedOnlineTests() {
    return axios.get(`${baseApiUrl}/online-tests/quiz/featured/`, postConfig())
  }

  // mainly for showing them on the front page
  static getFeaturedItems() {
    return axios.all([
      Api.getFeaturedCourses(),
      Api.getFeaturedEvents(),
      // Api.getFeaturedOnlineTests(),
    ])
  }

  static getUserCertificates(queryString) {
    return axios.get(
      `${baseApiUrl}/events/certificates/list/${prependQuery(queryString)}`,
      postConfig()
    )
  }

  static getEventsList(all, queryString) {
    if (all) {
      return axios.get(`${baseApiUrl}/events/event/list?all=1/`, postConfig())
    } else {
      return axios.get(
        `${baseApiUrl}/events/event/list/${prependQuery(queryString)}`,
        postConfig()
      )
    }
  }

  static getStatsOfService(serviceId) {
    return axios.get(`${baseApiUrl}/stats/service/${serviceId}/`, postConfig())
  }

  static getStaticPageContents(pageName) {
    // page name starts with a '/'
    return axios.get(`${baseApiUrl}/common/pages/page${pageName}/`, generalConfig)
  }

  static getStaticHeader() {
    // page name starts with a '/'
    return axios.get(`${baseApiUrl}/common/pages/`, generalConfig)
  }

  //
  // ─── SCHOOL ACTIONS ──────────────────────────────────────────────
  //

  // schools can batch import students using an excel file
  static importStudents(formData) {
    return axios.post(`${baseApiUrl}/profiles/students/import/`, formData, postConfig())
  }

  static addStudentsToSchool(formData, schoolId) {
    // formData must contain: array of students, their grade and their academic year
    return axios.post(
      `${baseApiUrl}/profiles/schools/${schoolId}/students/add/`,
      formData,
      postConfig()
    )
  }

  static addSingleStudentToSchool(formData) {
    return axios.post(
      `${baseApiUrl}/profiles/students/import/single/`,
      formData,
      postConfig()
    )
  }

  static fetchListOfStudentsOfSchool(schoolId, queryString) {
    return axios.get(
      `${baseApiUrl}/profiles/schools/${schoolId}/students/${prependQuery(queryString)}`,
      postConfig()
    )
  }

  static removeStudentFormSchool(schoolId, formData) {
    // formData contains a list of students
    return axios.post(
      `${baseApiUrl}/profiles/schools/${schoolId}/students/remove/`,
      formData,
      postConfig()
    )
  }

  //
  // ─── COURSE ACTIONS ──────────────────────────────────────────────
  //

  static getCoursesList(queryString) {
    return axios.get(
      `${baseApiUrl}/courses/course/list/${prependQuery(queryString)}`,
      postConfig()
    )
  }

  static getSingleCourse(courseId) {
    return axios.get(`${baseApiUrl}/courses/course/${courseId}/`, postConfig())
  }

  static getSingleCourseFull(courseId) {
    return axios.get(`${baseApiUrl}/courses/course/${courseId}/full/`, postConfig())
  }

  static getUserCourses(queryString) {
    return axios.get(
      `${baseApiUrl}/courses/course/mine/list/${prependQuery(queryString)}`,
      postConfig()
    )
  }

  // courses on this list are ordered by the student or school but they could
  // be in any state, like pending, completed, ...
  static getBoughtCourses() {
    return axios.get(`${baseApiUrl}/courses/order/mine/list/`, postConfig())
  }

  static createCourse(formData) {
    return axios.post(`${baseApiUrl}/courses/course/`, formData, postConfig())
  }

  static updateCourseInfo(formData, courseId) {
    return axios.patch(
      `${baseApiUrl}/courses/course/${courseId}/update/`,
      formData,
      postConfig()
    )
  }

  static getContentOfCourse(courseId) {
    return axios.get(
      `${baseApiUrl}/courses/course/${courseId}/contents/list/`,
      postConfig()
    )
  }

  static attachContentToCourse(formData, courseId) {
    return axios.post(
      `${baseApiUrl}/courses/course/${courseId}/contents/`,
      formData,
      postConfig()
    )
  }

  static updateContentOfCourse(attachmentId, courseId, formData) {
    return axios.patch(
      `${baseApiUrl}/courses/course/${courseId}/contents/content/${attachmentId}/`,
      formData,
      postConfig()
    )
  }

  static deleteContentOfCourse(attachmentId, courseId) {
    return axios.delete(
      `${baseApiUrl}/courses/course/${courseId}/contents/content/${attachmentId}/`,
      postConfig()
    )
  }

  static createCourseOrder(formData) {
    return axios.post(`${baseApiUrl}/courses/order/`, formData, postConfig())
  }

  //
  // ─── MANAGER SPECIFIC ACTIONS ────────────────────────────────────
  //

  static removeUserFromSystem(userType, id) {
    // only a manager can do this
    switch (userType) {
      case 'SCHOOL':
        return axios.delete(`${baseApiUrl}/profiles/schools/delete/${id}/`, postConfig())

      case 'TEACHER':
        return axios.delete(`${baseApiUrl}/profiles/teachers/delete/${id}/`, postConfig())

      case 'SUPERVISOR':
        return axios.delete(
          `${baseApiUrl}/profiles/supervisors/delete/${id}/`,
          postConfig()
        )

      case 'APP':
        return axios.delete(`${baseApiUrl}/apps/application/${id}/`, postConfig())

      // case "STUDENT":
      //   return axios.delete(`${baseApiUrl}/profiles/schools/delete/${id}/`, formData, postConfig())
      //   break;

      default:
        Promise.reject({ error: 'check user types!' })
    }
  }

  static changeUserVerificationStatus(userType, userId, formData) {
    if (userType !== 'MANAGER') {
      switch (userType) {
        case 'SCHOOL':
          return axios.patch(
            `${baseApiUrl}/profiles/schools/verify/${userId}/`,
            formData,
            postConfig()
          )

        case 'TEACHER':
          return axios.patch(
            `${baseApiUrl}/profiles/teachers/verify/${userId}/`,
            formData,
            postConfig()
          )

        default:
          console.error('check userType !')
          break
      }
    }
  }

  //
  // ─── STUDENT ACTIONS ─────────────────────────────────────────────
  //

  static uploadStudentDocument(formData) {
    return axios.post(
      `${baseApiUrl}/profiles/students/documents/`,
      formData,
      postConfig()
    )
  }

  static getStudentDocuments(userId) {
    return axios.get(
      `${baseApiUrl}/profiles/students/student/${userId}/documents/`,
      postConfig()
    )
  }

  static removeStudentDocument(documentId) {
    return axios.delete(
      `${baseApiUrl}/profiles/students/documents/document/${documentId}/`,
      postConfig()
    )
  }

  //
  // ─── SUPERVISOR ACTIONS ──────────────────────────────────────────
  //

  static fetchListOfStudentsOfSupervisor(supervisorId) {
    return axios.get(
      `${baseApiUrl}/profiles/supervisors/${supervisorId}/students/`,
      postConfig()
    )
  }

  static removeStudentFormSupervisor(supervisorId, formData) {
    // formData contains a list of students
    return axios.post(
      `${baseApiUrl}/profiles/supervisors/${supervisorId}/students/remove/`,
      formData,
      postConfig()
    )
  }

  static addStudentsToSupervisor(formData, supervisorId) {
    // formData must contain: array of students, their grade and their academic year
    return axios.post(
      `${baseApiUrl}/profiles/supervisors/${supervisorId}/students/add/`,
      formData,
      postConfig()
    )
  }

  //
  // ─── EVENTS ACTIONS ───────────────────────────────────────────────
  //

  static createEvent(formData) {
    return axios.post(`${baseApiUrl}/events/event/`, formData, postConfig())
  }

  static editEvent(eventId, formData) {
    return axios.patch(
      `${baseApiUrl}/events/event/${eventId}/update/`,
      formData,
      postConfig()
    )
  }

  static getSingleEvent(eventId) {
    return axios.get(`${baseApiUrl}/events/event/${eventId}`, postConfig())
  }

  static getEventCategories() {
    return axios.get(`${baseApiUrl}/events/event-categories/`, postConfig())
  }

  static getUserTicketOrders() {
    return axios.get(`${baseApiUrl}/events/ticket-order/mine/list/`, postConfig())
  }

  static purchaseEvent(formData) {
    return axios.post(`${baseApiUrl}/events/ticket-order/`, formData, postConfig())
  }

  static checkEventPrice(formData) {
    return axios.post(`${baseApiUrl}/events/ticket-order/price/`, formData, postConfig())
  }

  static getTopStudentsForEvent(formData) {
    return axios.post(`${baseApiUrl}/events/event/check-top/`, formData, postConfig())
  }

  static deleteTicket(ticketId) {
    return axios.delete(`${baseApiUrl}/orders/ticket/${ticketId}/delete/`, postConfig())
  }

  static deleteExtensionFromEvent(extensionId) {
    return axios.delete(
      `${baseApiUrl}/orders/extension/${extensionId}/delete/`,
      postConfig()
    )
  }

  static deleteTicketFromEvent(ticketId) {
    return axios.delete(`${baseApiUrl}/orders/ticket/${ticketId}/delete/`, postConfig())
  }

  static getEventTemplatesForCertificates() {
    return axios.get(`${baseApiUrl}/events/templates/certificates`, postConfig())
  }

  static getEventTemplatesForBadges() {
    return axios.get(`${baseApiUrl}/events/templates/badges`, postConfig())
  }

  static getEventTemplatesForAppreciationLetters() {
    return axios.get(`${baseApiUrl}/events/templates/appreciation`, postConfig())
  }

  static getEventTemplatesForResults() {
    return axios.get(`${baseApiUrl}/events/templates/results`, postConfig())
  }

  static getAllEventTemplates() {
    return Promise.all([
      Api.getEventTemplatesForBadges(),
      Api.getEventTemplatesForCertificates(),
      Api.getEventTemplatesForResults(),
      // Api.getEventTemplatesForAppreciationLetters(),
    ])
  }

  static fetchBoughtEvents() {
    return axios.get(`${baseApiUrl}/events/event/bought`, postConfig())
  }

  //
  // ─── EDUCATION ACTIONS ───────────────────────────────────────────
  //

  static addCourseTopic(formData) {
    return axios.post(`${baseApiUrl}/educations/course-topics/`, formData, postConfig())
  }

  //
  // ─── MASSAGING ACTIONS ───────────────────────────────────────────
  //

  static fetchSentMessages(queryString) {
    return axios.get(
      `${baseApiUrl}/messages/message/sent/list/${prependQuery(queryString)}`,
      postConfig()
    )
  }

  static fetchReceivedMessages(queryString) {
    return axios.get(
      `${baseApiUrl}/messages/message/received/list/all/${prependQuery(queryString)}`,
      postConfig()
    )
  }

  static fetchSingleMessages(messageId) {
    return axios.get(`${baseApiUrl}/messages/message/${messageId}/`, postConfig())
  }

  static fetchNotifications() {
    return axios.get(`${baseApiUrl}/messages/message/received/list/new/`, postConfig())
  }

  static setSeenStatus(data) {
    return axios.post(`${baseApiUrl}/messages/seen/`, data, postConfig())
  }

  static sentMessage(formData) {
    return axios.post(`${baseApiUrl}/messages/message/`, formData, postConfig())
  }

  static broadcastMessage(formData) {
    return axios.post(`${baseApiUrl}/messages/broadcast/`, formData, postConfig())
  }

  static getBroadcastedMessages(queryString) {
    return axios.get(
      `${baseApiUrl}/messages/broadcast/list/${prependQuery(queryString)}`,
      postConfig()
    )
  }

  static fetchSingleBroadcast(id) {
    return axios.get(`${baseApiUrl}/messages/broadcast/${id}/`, postConfig())
  }

  static getUserBroadcasts(queryString) {
    return axios.get(
      `${baseApiUrl}/messages/broadcast/received/list/all/${prependQuery(queryString)}`,
      postConfig()
    )
  }

  static getUserNewBroadcasts() {
    return axios.get(`${baseApiUrl}/messages/broadcast/received/list/new/`, postConfig())
  }

  static getManagerEmails(queryString) {
    return axios.get(
      `${baseApiUrl}/messages/email/list/${prependQuery(queryString)}`,
      postConfig()
    )
  }

  static getManagerSms(queryString) {
    return axios.get(
      `${baseApiUrl}/messages/sms/list/${prependQuery(queryString)}`,
      postConfig()
    )
  }

  static sendEmail(formData) {
    return axios.post(`${baseApiUrl}/messages/email/`, formData, postConfig())
  }

  static sendSms(formData) {
    return axios.post(`${baseApiUrl}/messages/sms/`, formData, postConfig())
  }

  static fetchSingleEmail(id) {
    return axios.get(`${baseApiUrl}/messages/email/${id}/`, postConfig())
  }

  //
  // ─── ORDERS AND PAYMENT ACTIONS ──────────────────────────────────
  //

  static createOrderForService(formData) {
    return axios.post(`${baseApiUrl}/orders/order/`, formData, postConfig())
  }

  static updateOrder(orderId, formData) {
    return axios.patch(
      `${baseApiUrl}/orders/order/${orderId}/update/`,
      formData,
      postConfig()
    )
  }

  static fetchBuyOrders() {
    return axios.get(`${baseApiUrl}/orders/order/bought/list/`, postConfig())
  }

  static fetchSingleOrder(orderId) {
    return axios.get(`${baseApiUrl}/orders/order/${orderId}/`, postConfig())
  }

  static payPendingOrder(orderId) {
    return axios.put(`${baseApiUrl}/orders/order/${orderId}/pay/`, {}, postConfig())
  }

  static payOrder(formData) {
    return axios.post(`${baseApiUrl}/orders/charge/request/`, formData, postConfig())
  }

  static cancelPendingOrder(orderId) {
    return axios.patch(`${baseApiUrl}/orders/order/${orderId}/cancel/`, {}, postConfig())
  }

  static applyDiscount(formData, userType, serviceType) {
    // service_code, promo_code owners

    const executeDefaultPath = () =>
      axios.post(`${baseApiUrl}/orders/order/check/`, formData, postConfig())

    const executeEventTicketDiscount = () =>
      axios.post(`${baseApiUrl}/events/ticket-order/check/`, formData, postConfig())

    switch (userType) {
      case 'SCHOOL':
        if (serviceType === 'EVENT') {
          return executeEventTicketDiscount()
        } else {
          return executeDefaultPath()
        }

      default:
        return executeDefaultPath()
    }
  }

  //
  // ─── CREDIT AND PAYMENTS ACTIONS ─────────────────────────────────
  //

  static getUserBalance(userId) {
    return axios.get(`${baseApiUrl}/profiles/balance/${userId}/`, postConfig())
  }

  static requestPayment(formData) {
    return axios.post(`${baseApiUrl}/orders/charge/request/`, formData, postConfig())
  }

  static confirmRecharge(formData) {
    // formData contains only 1 field => "tref"
    // here it comes from localStorage.
    return axios.post(`${baseApiUrl}/orders/charge/confirm/`, formData, postConfig())
  }

  //
  // ─── STATS ACTIONS ───────────────────────────────────────────────
  //

  static getUserStats(userType, userId) {
    switch (userType) {
      case 'STUDENT':
        return axios.get(`${baseApiUrl}/stats/student/${userId}/`, postConfig())

      case 'TEACHER':
        return axios.get(`${baseApiUrl}/stats/teacher/${userId}/`, postConfig())

      case 'SCHOOL':
        // to fetch the overall stats of students
        return axios.get(`${baseApiUrl}/stats/students/`, postConfig())

      case 'SUPERVISOR':
        // to fetch the overall stats of students
        return axios.get(`${baseApiUrl}/stats/students/`, postConfig())

      case 'MANAGER':
        return axios.get(`${baseApiUrl}/stats/overall/`, postConfig())

      default:
        return Promise.reject('please check the userType')
    }
  }

  static getOverallStats() {
    // only for manager
    return axios.get(`${baseApiUrl}/stats/overall/`, postConfig())
  }

  //
  // ─── ONLINE TEST ACTIONS ─────────────────────────────────────────
  //

  static createQuestion(formData) {
    return axios.post(`${baseApiUrl}/online-tests/question/`, formData, postConfig())
  }

  static fetchListOfQuestions(queryString) {
    return axios.get(
      `${baseApiUrl}/online-tests/questions/${prependQuery(queryString)}`,
      postConfig()
    )
  }

  static createOnlineTest(formData) {
    return axios.post(`${baseApiUrl}/online-tests/quiz/`, formData, postConfig())
  }

  static addTopicsToTest(formData, testId) {
    return axios.put(
      `${baseApiUrl}/online-tests/quiz/${testId}/courses/add/`,
      formData,
      postConfig()
    )
  }

  static addQuestionsToTest(formData, testId) {
    return axios.patch(
      `${baseApiUrl}/online-tests/quiz/${testId}/questions/add/`,
      formData,
      postConfig()
    )
  }

  static fetchReportOfCreatedTest(testId) {
    return axios.get(`${baseApiUrl}/online-tests/quiz/${testId}/export/`, postConfig())
  }

  static fetchListOfTests(queryString) {
    return axios.get(
      `${baseApiUrl}/online-tests/quiz/list/${prependQuery(queryString)}`,
      postConfig()
    )
  }

  static fetchSingleTest(testId) {
    return axios.get(`${baseApiUrl}/online-tests/quiz/${testId}/`, postConfig())
  }

  static createTestTicket(formData) {
    // formData: service_code promo_code owners
    return axios.post(`${baseApiUrl}/online-tests/ticket-order/`, formData, postConfig())
  }

  static fetchUserTestTickets() {
    return axios.get(`${baseApiUrl}/online-tests/ticket-orders/mine/`, postConfig())
  }

  static checkStatusOfTest(testId) {
    return axios.get(`${baseApiUrl}/online-tests/quiz/${testId}/check/`, postConfig())
  }

  static takeTheTest(testId) {
    return axios.get(`${baseApiUrl}/online-tests/quiz/${testId}/take/`, postConfig())
  }

  static submitTestAnswers(data) {
    return axios.post(`${baseApiUrl}/online-tests/quiz/submit/`, data, postConfig())
  }

  static getTestResult(testId) {
    return axios.get(`${baseApiUrl}/online-tests/quiz/${testId}/result/`, postConfig())
  }

  static removeQuestionFormTest(questionId, testId) {
    const formData = appendToFormData({
      quiz_questions: JSON.stringify([{ question: questionId }]),
    })
    return axios.put(
      `${baseApiUrl}/online-tests/quiz/${testId}/questions/remove/`,
      formData,
      postConfig()
    )
  }

  static fetchContactsInfo() {
    return axios.get(`${baseApiUrl}/messages/contact/`, postConfig())
  }

  // Support and Ticketing
  static createSupportTicket(formData) {
    return axios.post(
      `${baseApiUrl}/support/tickets/`,
      formData,
      postConfig()
    )
  }

  static getSupportTickets(userType, queryString) {
    switch(userType) {
      case 'MANAGER':
        return axios.get(
          `${baseApiUrl}/support/manager/tickets/list/${prependQuery(queryString)}`,
          postConfig()
        )

      default:
        return axios.get(
          `${baseApiUrl}/support/tickets/list/${prependQuery(queryString)}`,
          postConfig()
        )
    }
  }

  static getSingleSupportTicket(ticket_code, userType) {
    switch(userType) {
      case 'MANAGER':
        return axios.get(
          `${baseApiUrl}/support/manager/tickets/ticket/${ticket_code}/`,
          postConfig()
        )
      default:
        return axios.get(
          `${baseApiUrl}/support/tickets/ticket/${ticket_code}/`,
          postConfig()
        )
    }
  }

  static editSupportTicket(ticket_code, formData) {
    // only manager
    return axios.patch(
      `${baseApiUrl}/support/manager/tickets/ticket/${ticket_code}/edit/`,
      formData,
      postConfig()
    )
  }

  static replySupportTicket(ticket_code, formData, userType) {
    switch(userType) {
      case 'MANAGER':
        return axios.post(
          `${baseApiUrl}/support/manager/tickets/ticket/${ticket_code}/reply/`,
          formData,
          postConfig()
        )
      default:
        return axios.post(
          `${baseApiUrl}/support/tickets/ticket/${ticket_code}/reply/`,
          formData,
          postConfig()
        )
    }
  }

  static getSupportReceiversList(queryString) {
    return axios.get(
      `${baseApiUrl}/profiles/search/${prependQuery(queryString)}`,
      postConfig()
    )
  }

  static getSupportTicketTypes() {
    return axios.get(
      `${baseApiUrl}/support/ticket-types`,
      postConfig(),
    )
  }

  // institute types
  static getInstituteTypes() {
    return axios.get(
      `${baseApiUrl}/profiles/schools/institute-types/`,
      generalConfig,
    )
  }

  // supervisor endpoints
  static fetchSupervisorEvents() {
    return axios.get(
      `${baseApiUrl}/events/supervisor/event/list/`,
      postConfig(),
    )
  }

  static fetchSupervisorEventHosts(eventId, queryString = '') {
    return axios.get(
      `${baseApiUrl}/events/supervisor/event/${eventId}/hosts/${prependQuery(queryString)}`,
      postConfig(),
    )
  }

  static fetchSupervisorEventStudents(eventId, queryString) {
    return axios.get(
      `${baseApiUrl}/events/supervisor/event/${eventId}/students/${prependQuery(queryString)}`,
      postConfig(),
    )
  }

  static fetchSupervisorEventSchools(eventId, queryString) {
    return axios.get(
      `${baseApiUrl}/events/supervisor/event/${eventId}/schools/${prependQuery(queryString)}`,
      postConfig(),
    )
  }

  static fetchSupervisorSchoolEventStudents(eventId, schoolId) {
    return axios.get(
      `${baseApiUrl}/events/supervisor/event/${eventId}/schools/school/${schoolId}/students/`,
      postConfig(),
    )
  }

  static fetchSupervisorHost(hostId) {
    return axios.get(
      `${baseApiUrl}/events/supervisor/host/${hostId}/`,
      postConfig(),
    )
  }

  static supervisorAddHost(eventId, formData) {
    return axios.post(
      `${baseApiUrl}/events/supervisor/event/${eventId}/hosts/add/`,
      formData,
      postConfig(),
    )
  }

  static supervisorRemoveHost(hostId) {
    return axios.delete(
      `${baseApiUrl}/events/supervisor/host/${hostId}/edit/`,
      postConfig(),
    )
  }

  static editSupervisorHost(hostId, formData) {
    return axios.patch(
      `${baseApiUrl}/events/supervisor/host/${hostId}/edit/`,
      formData,
      postConfig(),
    )
  }

  static addStudentsToSupervisorHost(hostId, formData) {
    return axios.post(
      `${baseApiUrl}/events/supervisor/host/${hostId}/students/add/`,
      formData,
      postConfig(),
    )
  }

  static removeStudentsFromSupervisorHost(hostId, formData) {
    return axios.post(
      `${baseApiUrl}/events/supervisor/host/${hostId}/students/remove/`,
      formData,
      postConfig(),
    )
  }

  static supervisorFinishEventHosting(eventId) {
    return axios.post(
      `${baseApiUrl}/events/supervisor/event/${eventId}/finish-hosting/`,
      null,
      postConfig(),
    )
  }

  static supervisorFetchEventDocuments(eventId) {
    return axios.get(
      `${baseApiUrl}/support/supervisor/event/${eventId}/ticket/`,
      postConfig(),
    )
  }

  // promo codes

  static fetchPromoCodes(eventId) {
    const queryString = eventId? `service_id=${eventId}` : ''

    return axios.get(
      `${baseApiUrl}/orders/promo-codes/list/${prependQuery(queryString)}`,
      postConfig(),
    )
  }

  static fetchSinglePromoCode(id) {
    return axios.get(
      `${baseApiUrl}/orders/promo-codes/promo-code/${id}/`,
      postConfig(),
    )
  }

  static addPromoCode(formData) {
    return axios.post(
      `${baseApiUrl}/orders/promo-codes/add/`,
      formData,
      postConfig(),
    )
  }

  static updatePromoCode(id, formData) {
    return axios.patch(
      `${baseApiUrl}/orders/promo-codes/promo-code/${id}/update/`,
      formData,
      postConfig(),
    )
  }

  // gamification

  static fetchStudentBadges() {
    return axios.get(
      `${baseApiUrl}/gamification/badge/list/`,
      postConfig(),
    )
  }
}



// WEBPACK FOOTER //
// ./src/api/api.js